<!--
  자료실 — 도서 목록 (guide01.md §5, 승인 강사 전용)
  · GET /instructor/materials/books?search=&limit=&offset= (서버사이드 검색/페이징)
  · 표지 그리드 (cover_image_url — 공개 URL), 클릭 시 상세 페이지 전환
  · 403 = 미승인(구 토큰) → 승인 대기 안내 패널로 대체
-->
<template>
  <div>
    <a-page-head title="자료실" :breadcrumbs="[{ label: '자료실' }, { label: '도서 목록' }]" />

    <!-- 미승인(403) 안내 -->
    <a-card v-if="forbidden" bare>
      <a-empty
        title="강사 승인 대기 중입니다"
        sub="자료실은 승인된 강사만 이용할 수 있습니다. 승인 완료 후 다시 로그인하면 이용하실 수 있습니다."
      >
        <template #action>
          <a-btn kind="primary" @click="$router.push({ name: 'InstructorAccessStatus' })">요청 상태 확인</a-btn>
        </template>
      </a-empty>
    </a-card>

    <a-card v-else bare>
      <!-- 검색 바 -->
      <div class="p-books__toolbar">
        <div class="p-books__search">
          <input
            v-model.trim="search"
            type="text"
            class="p-books__search-input"
            placeholder="도서명, 출판사 검색"
            @keyup.enter="onSearch"
          />
          <a-btn kind="primary" @click="onSearch">검색</a-btn>
        </div>
        <div class="p-books__count p-mono">총 {{ totalCount }}권</div>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="p-books__loading">자료를 불러오는 중입니다…</div>

      <!-- 빈 상태 -->
      <a-empty
        v-else-if="!books.length"
        :title="appliedSearch ? '검색 결과가 없습니다' : '등록된 자료가 없습니다'"
        :sub="appliedSearch ? '다른 검색어로 시도해보세요.' : '자료가 등록되면 이곳에 표시됩니다.'"
      />

      <!-- 표지 그리드 -->
      <div v-else class="p-books__grid">
        <div
          v-for="book in books"
          :key="book.idx"
          class="p-books__card"
          @click="goDetail(book)"
        >
          <div class="p-books__cover">
            <img
              v-if="!coverErrors[book.idx]"
              :src="book.cover_image_url"
              :alt="book.title"
              loading="lazy"
              @error="coverErrors[book.idx] = true"
            />
            <div v-else class="p-books__cover-fallback">📘</div>
          </div>
          <div class="p-books__info">
            <div class="p-books__title" :title="book.title">{{ book.title }}</div>
            <div class="p-books__meta">
              <a-pill v-if="book.level" kind="yellow" :dot="false">{{ book.level }}</a-pill>
              <span v-if="book.publisher" class="p-books__publisher">{{ book.publisher }}</span>
            </div>
            <div class="p-books__date p-mono">{{ $formatDate(book.created_at) }}</div>
          </div>
        </div>
      </div>

      <!-- 페이지네이션 -->
      <div v-if="totalPages > 1" class="p-pagination">
        <button class="p-page-btn" :disabled="page === 1" @click="changePage(page - 1)">‹</button>
        <button
          v-for="p in totalPages"
          :key="p"
          class="p-page-btn"
          :class="{ 'is-active': page === p }"
          @click="changePage(p)"
        >{{ p }}</button>
        <button class="p-page-btn" :disabled="page === totalPages" @click="changePage(page + 1)">›</button>
      </div>
    </a-card>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';

export default {
  name: 'MaterialsBooks',
  components: { APageHead, ACard, AEmpty, ABtn, APill },
  data() {
    return {
      books: [],
      totalCount: 0,
      search: '',
      appliedSearch: '',
      page: 1,
      limit: 50,
      loading: false,
      forbidden: false,
      coverErrors: {},
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.limit));
    },
  },
  mounted() {
    this.fetchBooks();
  },
  methods: {
    async fetchBooks() {
      this.loading = true;
      this.forbidden = false;
      try {
        const res = await this.$axios({
          method: 'get',
          url: '/instructor/materials/books',
          params: {
            search: this.appliedSearch,
            limit: this.limit,
            offset: (this.page - 1) * this.limit,
          },
        });
        this.books = res.data.items || [];
        this.totalCount = res.data.total_count || 0;
        this.coverErrors = {};
      } catch (e) {
        if (e.response?.status === 403) {
          // 미승인 계정 (승인 전 발급된 구 토큰 등)
          this.forbidden = true;
        } else {
          console.error('자료실 목록 조회 실패:', e);
          this.$notifyVue({ type: 'error', title: '자료실', text: '목록을 불러오지 못했습니다.' });
        }
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.appliedSearch = this.search;
      this.page = 1;
      this.fetchBooks();
    },
    changePage(p) {
      if (p < 1 || p > this.totalPages || p === this.page) return;
      this.page = p;
      this.fetchBooks();
    },
    goDetail(book) {
      this.$router.push({ name: 'MaterialsBooksDetail', query: { barcode: book.barcode } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink-soft; }

.p-books__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid $p-line;
}
.p-books__search {
  display: flex;
  gap: 8px;
  flex: 1;
  max-width: 420px;
}
.p-books__search-input {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: $p-panel;
  font-family: $p-font-body;
  font-size: 12px;
  color: $p-ink;
  outline: none;

  &:focus { border-color: $p-point; box-shadow: 0 0 0 3px rgba(255, 201, 60, 0.2); }
  &::placeholder { color: $p-ink-soft; }
}
.p-books__count { flex-shrink: 0; }

.p-books__loading {
  padding: 48px;
  text-align: center;
  font-size: 13px;
  color: $p-ink-mid;
}

.p-books__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  padding: 20px;
}
.p-books__card {
  border: 1px solid $p-line;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: $p-panel;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: $p-point;
    box-shadow: 0 12px 24px -12px rgba(31, 27, 20, 0.25);
    transform: translateY(-2px);
  }
}
.p-books__cover {
  aspect-ratio: 3 / 4;
  background: $p-line-soft;
  display: grid;
  place-items: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.p-books__cover-fallback {
  font-size: 40px;
  opacity: 0.5;
}
.p-books__info {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.p-books__title {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: $p-ink;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.p-books__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.p-books__publisher {
  font-size: 11px;
  color: $p-ink-mid;
}
.p-books__date { font-size: 10px; }

.p-pagination {
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 16px;
  border-top: 1px solid $p-line;
}
.p-page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid $p-line;
  background: $p-panel;
  border-radius: 6px;
  font-family: $p-font-mono;
  font-size: 12px;
  color: $p-ink2;
  cursor: pointer;
  &:hover:not(:disabled) { background: $p-bg; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &.is-active { background: $p-ink; color: #fff; border-color: $p-ink; }
}
</style>
